import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  maxWithdrawal,
  minPayout,
  money,
  newKey,
  WHOLE_DOLLARS,
  withdrawalValue,
  type Balances,
  type RefusedReason,
  type WithdrawOutcome,
} from './statementsData'

interface Props {
  balances: Balances
  /** Sends /v1/payouts/requests/create; never throws (a lost answer is `unknown`). */
  submit: (amount: number, idempotencyKey: string) => Promise<WithdrawOutcome>
  /** OnlyFans took it (or the answer was lost): the page closes the modal and refreshes. */
  onFinished: (outcome: WithdrawOutcome) => void
  onClose: () => void
}

type Problem = RefusedReason | { type: 'wholeDollars' }

/** OnlyFans' "Manual payouts" modal: a whole-dollar amount between the minimum and the available balance. */
export function WithdrawModal({ balances, submit, onFinished, onClose }: Props) {
  const { t } = useTranslation('ofStatements')
  const currency = balances.currency || 'USD'
  const min = minPayout(balances)
  const max = maxWithdrawal(balances)
  const [amount, setAmount] = useState('')
  const [problem, setProblem] = useState<Problem | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // One idempotency key per opened modal: a double click or a retried HTTP
  // call can never turn into a second withdrawal.
  const key = useRef(newKey('withdraw'))
  const input = useRef<HTMLInputElement | null>(null)
  const busy = useRef(false)

  useEffect(() => {
    input.current?.focus()
  }, [])

  const close = () => {
    if (!busy.current) onClose()
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy.current) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const value = withdrawalValue(amount)
  const canSubmit = !submitting && value > 0 && value >= min && value <= max

  const onInput = (raw: string) => {
    const next = raw.trim()
    if (next && !WHOLE_DOLLARS.test(next)) {
      setProblem({ type: 'wholeDollars' })
      return
    }
    setAmount(next.replace(/^0+(?=\d)/, ''))
    setProblem(null)
  }

  const setMax = () => {
    setAmount(max ? String(max) : '')
    setProblem(null)
    input.current?.focus()
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit || busy.current) return
    busy.current = true
    setSubmitting(true)
    setProblem(null)
    const outcome = await submit(value, key.current)
    busy.current = false
    setSubmitting(false)
    if (outcome.kind === 'refused') {
      // OnlyFans gave a definite answer: the next attempt is a new request.
      key.current = newKey('withdraw')
      setProblem(outcome.reason)
      return
    }
    onFinished(outcome)
  }

  const problemNode = (() => {
    if (!problem) return null
    switch (problem.type) {
      case 'wholeDollars':
        return t('withdraw.wholeDollars')
      case 'noPermission':
        return t('withdraw.noPermission')
      case 'http':
        return t('withdraw.failedHttp', { status: problem.status })
      case 'text':
        return problem.text
      case 'verify':
        return (
          <>
            {t('withdraw.verifyNeeded')}{' '}
            <a href={problem.url} target="_blank" rel="noopener noreferrer">
              {t('withdraw.verifyLink')}
            </a>
          </>
        )
    }
  })()

  return (
    <div className="ofp-modal-back" onClick={close}>
      <form
        className="ofp-modal ofs-withdraw"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ofs-withdraw-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h3 className="ofp-modal-title" id="ofs-withdraw-title">
          {t('withdraw.title')}
        </h3>
        <div className="ofs-withdraw-row">
          <label className="ofs-withdraw-input">
            <span aria-hidden="true">$</span>
            <input
              ref={input}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={t('withdraw.amount')}
              aria-label={t('withdraw.amount')}
              aria-describedby="ofs-withdraw-hint"
              value={amount}
              disabled={submitting}
              onChange={(e) => onInput(e.target.value)}
            />
          </label>
          <button type="button" className="ofs-max-btn" disabled={submitting || value === max} onClick={setMax}>
            {t('withdraw.max', { max: money(max, currency, 0) })}
          </button>
        </div>
        <div className="ofs-withdraw-hint" id="ofs-withdraw-hint">
          {t('withdraw.minHint', { min: money(min, currency, 0), cur: currency })}
        </div>
        {problemNode && (
          <div className="ofs-withdraw-error" role="alert">
            {problemNode}
          </div>
        )}
        <div className="ofp-modal-actions">
          <button type="button" className="lib-btn lib-btn--ghost" onClick={close} disabled={submitting}>
            {t('withdraw.cancel')}
          </button>
          <button type="submit" className="lib-btn lib-btn--primary" disabled={!canSubmit}>
            {submitting ? t('withdraw.submitting') : t('withdraw.submit')}
          </button>
        </div>
      </form>
    </div>
  )
}
